import { useEffect, useRef } from "react";
import { Camera, SendHorizontal, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { VoiceRecorderButton } from "@/components/chat/VoiceRecorderButton";
import { toast } from "sonner";

interface Props {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  image?: File | null;
  imagePreviewUrl?: string | null;
  onImageChange?: (file: File | null) => void;
}

const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10MB
const ACCEPTED_IMAGE_TYPES = /^image\/(jpe?g|png|gif|webp|svg\+xml)$/i;

async function compressImage(file: File, maxSize: number, quality: number): Promise<File> {
  // SVG e GIF non vengono ricompressi (perderebbero animazione / vettorialità).
  if (/svg|gif/i.test(file.type)) return file;
  try {
    const bitmap = await createImageBitmap(file);
    const ratio = Math.min(maxSize / bitmap.width, maxSize / bitmap.height, 1);
    const w = Math.round(bitmap.width * ratio);
    const h = Math.round(bitmap.height * ratio);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);
    const blob: Blob | null = await new Promise((res) =>
      canvas.toBlob((b) => res(b), "image/jpeg", quality),
    );
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.\w+$/, ".jpg"), {
      type: "image/jpeg",
    });
  } catch {
    return file;
  }
}

export function ChatInput({
  value,
  onChange,
  onSubmit,
  disabled,
  image,
  imagePreviewUrl,
  onImageChange,
}: Props) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* Definito qui sopra e non piu' giu' vicino al `return`: l'effetto che
     misura l'altezza lo usa fra le dipendenze, e una `const` letta prima della
     sua riga fa esplodere il render. */
  /* Due frasi corte, ma la lunghezza NON e' piu' cio' che tiene in piedi il
     layout (27/08/2026, corretto in giornata).
     Prima qui c'era scritto che bastava stare entro ~23 caratteri. Era una
     diagnosi sbagliata, e Davide l'ha smentita dal suo iPhone: andava a capo
     anche "Scrivi la tua domanda...", che di caratteri ne ha 24 ma sul suo
     telefono non ci stava lo stesso. Misurato con il CSS vero a 393 punti:
     169 px di testo contro 187 disponibili - avanzavano 18 px, il 10%. Basta
     una tacca di "Testo piu' grande" nelle impostazioni di iOS (+10%) e il
     testo diventa 183 px: va a capo. Un margine cosi' sottile non e' una
     correzione, e' una coincidenza.
     Adesso a reggere sono le tre regole qui sotto - segnaposto che non va mai
     a capo, campo vuoto sempre alto una riga, bottoni ancorati al fondo - e la
     frase si puo' cambiare senza contare i caratteri. */
  const placeholder = image
    ? "Cosa vuoi sapere?"
    : "Scrivi la tua domanda...";

  /* IL CAMPO VUOTO E' SEMPRE ALTO UNA RIGA (27/08/2026, dal Mac).
     A campo vuoto non si misura piu' `scrollHeight`: quel numero non descrive
     il testo scritto - non ce n'e' - ma il SEGNAPOSTO, e se il segnaposto va a
     capo la barra cresce di una riga da sola, con il testo che finisce sotto
     il centro delle icone. Azzerando l'altezza torna a valere `rows={1}` piu'
     `min-h-[44px]`, che non dipendono da quanto e' lunga la frase ne' da
     quanto e' largo il telefono.
     Per questo `placeholder` e' uscito dalle dipendenze: adesso non tocca piu'
     l'altezza, quindi non c'e' niente da ricalcolare quando cambia. */
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!el.value) {
      el.style.height = "";
      return;
    }
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 180) + "px";
  }, [value]);

  const handleKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && value.trim()) onSubmit();
    }
  };

  const handleTranscribed = (text: string) => {
    const base = value;
    const sep = base && !base.endsWith(" ") ? " " : "";
    onChange((base + sep + text).trim());
    requestAnimationFrame(() => ref.current?.focus());
  };

  const onPickPhoto = () => {
    fileInputRef.current?.click();
  };

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    // reset così la stessa immagine può essere riselezionata
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!f) return;
    if (!ACCEPTED_IMAGE_TYPES.test(f.type)) {
      toast.error("Formato non supportato. Usa JPG, PNG, GIF, WEBP o SVG.");
      return;
    }
    if (f.size > MAX_IMAGE_BYTES) {
      toast.error("Immagine troppo grande (max 10MB).");
      return;
    }
    const compressed = await compressImage(f, 1024, 0.85);
    onImageChange?.(compressed);
  };

  const removeImage = () => onImageChange?.(null);

  const displayValue = value;
  /** Niente da mandare: e' il momento del microfono, non di «Invia». */
  const vuoto = !value.trim() && !image;

  return (
    <div className="mx-auto w-full max-w-3xl px-3 sm:px-5">
      {image && imagePreviewUrl && (
        <div className="mb-2 flex items-center">
          <div className="relative">
            <img
              src={imagePreviewUrl}
              alt="Anteprima"
              className="h-12 w-12 rounded-md object-cover ring-1 ring-border"
            />
            <button
              type="button"
              onClick={removeImage}
              aria-label="Rimuovi immagine"
              className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}
      {/* La barra deve farsi trovare: bordo col blu di casa e un alone morbido,
          non un'altra card grigia che si mimetizza in fondo (Davide, 26/08). */}
      {/* LA BARRA DOPO LA PROVA DI GIACOMO (11/09/2026: «i tre tasti sono
          troppo grandi, si prendono troppo della box di inserimento»).
          Misurato: tre tasti da 44 piu' gli spazi = 156 px, e al campo restavano
          189 px su 393. Ora i tasti sono DUE, da 40, con 4 px fra loro e la
          barra stretta a destra (pl-3.5 / pr-1.5): al campo restano ~259 px,
          +38%. Il terzo tasto non e' sparito: a campo vuoto c'e' il microfono
          (arancione, e' cosi' che in cantiere si fa una domanda), appena si
          scrive diventa «Invia» - come WhatsApp. Nessun menu, nessun tocco in
          piu'. Non si scende sotto i 40 px: sono dita sporche e guanti. */}
      <div className="flex items-end gap-1 rounded-2xl border border-primary/35 bg-surface-elevated py-1.5 pl-3.5 pr-1.5 shadow-[0_0_0_1px_hsl(var(--primary)/0.08),0_10px_32px_-14px_hsl(var(--primary)/0.5)] transition-colors focus-within:border-primary/70 focus-within:ring-2 focus-within:ring-primary/25">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          /* NIENTE `capture` (27/08/2026, dal Mac).
             Con capture="environment" iOS apriva la fotocamera e basta: non si
             poteva allegare una foto gia' scattata. Ma Info.plist promette
             l'opposto - NSPhotoLibraryUsageDescription dice "per allegare alla
             domanda una foto dell'impianto gia' presente sul telefono" - e in
             cantiere la foto della centrale spesso e' di un'ora prima.
             Senza l'attributo, iOS offre la scelta fra rullino e scatto. */
          hidden
          onChange={onFileChange}
        />
        {/* py-[7px] e min-h-[40px] non sono numeri a caso: 26 px di riga piu'
            7+7 di margine fanno esattamente i 40 dei bottoni (erano 9+9 = 44
            fino al 22/09/2026), cosi' con una riga sola il testo e le icone
            hanno lo stesso centro, e con piu' righe il campo cresce verso il
            basso mentre i bottoni restano ancorati al fondo (`items-end`
            sopra) invece di galleggiare a meta' altezza.
            Le tre regole `placeholder:` fanno l'altra meta' del lavoro: se la
            frase non ci sta, viene tagliata con i puntini invece di mandare a
            capo la barra. Un suggerimento puo' essere troncato, il layout no. */}
        <textarea
          ref={ref}
          value={displayValue}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKey}
          rows={1}
          placeholder={placeholder}
          className="max-h-44 min-h-[40px] min-w-0 flex-1 resize-none bg-transparent py-[7px] text-base leading-relaxed text-foreground placeholder:overflow-hidden placeholder:text-ellipsis placeholder:whitespace-nowrap placeholder:text-muted-foreground focus:outline-none sm:text-[15px]"
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={onPickPhoto}
              disabled={disabled || !!image}
              aria-label="Allega foto"
              className="h-10 w-10 shrink-0 rounded-xl text-muted-foreground hover:text-foreground"
            >
              <Camera className="h-[18px] w-[18px]" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">Allega foto</TooltipContent>
        </Tooltip>
        {/* Microfono e «Invia» sono lo stesso posto: a campo vuoto «Invia» non
            serve a niente, li' ci va la dettatura, arancione e grande; appena
            c'e' una lettera (o una foto) diventa «Invia». Chi ha gia' scritto
            e vuole dettare il resto cancella e ridetta: e' lo scambio di
            WhatsApp, e nessuno se ne lamenta. */}
        {vuoto ? (
          <VoiceRecorderButton disabled={disabled} onTranscribed={handleTranscribed} evidenza />
        ) : (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                onClick={onSubmit}
                disabled={disabled}
                className="h-10 w-10 shrink-0 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90"
                aria-label="Invia domanda"
              >
                <SendHorizontal className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">Invia domanda</TooltipContent>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
